import { useState, useMemo } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { useApiQuery, useApiMutation, queryClient } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Trash2, ChevronLeft, ChevronRight, Loader2, Plus, X, Radio, Navigation, Network, MapPin } from "lucide-react"
import type {
  SensingStation,
  NetworkSite,
  GetSensingStationsResponse,
  GetNetworkSitesResponse,
  GetNetworkCorridorsResponse,
} from "@/types/sensNet"
import { useToast } from "@/contexts/ToastContext"

export type SensingStationListProps = {
  onAdd?: () => void
  onEdit?: (station: SensingStation) => void
}

export function SensingStationList({ onAdd, onEdit }: SensingStationListProps = {}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [corridorFilter, setCorridorFilter] = useState<string>("all")
  const [siteFilter, setSiteFilter] = useState<string>("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedStation, setSelectedStation] = useState<SensingStation | null>(null)
  const itemsPerPage = 10
  const { toast } = useToast()

  const { data, isLoading, isError, refetch } = useApiQuery<GetSensingStationsResponse>("getSensingStations", {})
  const { data: sitesData } = useApiQuery<GetNetworkSitesResponse>("getNetworkSites", {})
  const { data: corridorsData } = useApiQuery<GetNetworkCorridorsResponse>("getNetworkCorridors", {})

  const allSites: NetworkSite[] = ((sitesData as any)?.content?.networkSites || (sitesData as any)?.networkSites || []) as unknown as NetworkSite[]
  const allCorridors = (corridorsData as any)?.content?.networkCorridors || (corridorsData as any)?.networkCorridors || []

  // Map API response to component state
  const stations: SensingStation[] = ((data as any)?.content?.stations || (data as any)?.stations || []).map((s: any) => ({
    id: s.id,
    stationNumber: s.stationNumber,
    name: s.stationName || s.name || '',
    geoLocation: s.location || s.geoLocation || { latitude: 0, longitude: 0 },
    addressIfAvailable: s.stationAddress || s.addressIfAvailable || '',
    stationRoadIntersections: s.stationRoadIntersections || '',
    stationNotes: s.stationNotes || '',
    coveredBySites: s.coveredBySites || [],
    stationPictures: s.stationPictures || [],
    createdAt: s.createdAt ?? 0,
    updatedAt: s.updatedAt ?? 0,
  }))

  // Sites filtered by selected corridor (for the site filter dropdown)
  const sitesForFilter = useMemo(() => {
    if (corridorFilter === "all") return allSites
    return allSites.filter((site) => (site as any).networkCorridorNumber?.toString() === corridorFilter)
  }, [allSites, corridorFilter])

  // Reset site filter when corridor changes
  const handleCorridorChange = (value: string) => {
    setCorridorFilter(value)
    setSiteFilter("all")
    setCurrentPage(1)
  }

  // Filter stations
  const filteredStations = useMemo(() => {
    return stations.filter((station) => {
      const matchesSearch = !searchQuery || station.name?.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesSite = siteFilter === "all" || station.coveredBySites?.some((s) => String(s) === siteFilter)
      const matchesCorridor = corridorFilter === "all" || station.coveredBySites?.some((siteId) => {
        const site = (allSites as any[]).find((s) => String(s.networkSiteNumber) === String(siteId))
        return site?.networkCorridorNumber?.toString() === corridorFilter
      })
      return matchesSearch && matchesSite && matchesCorridor
    })
  }, [stations, searchQuery, siteFilter, corridorFilter, allSites])

  // Build grouped structure: corridor → site → stations
  const grouped = useMemo(() => {
    type GroupedSite = { site: NetworkSite; stations: SensingStation[] }
    type GroupedCorridor = { corridorName: string; corridorId: string; sites: GroupedSite[] }
    const corridorMap = new Map<string, GroupedCorridor>()

    // "Unassigned" bucket for stations with no site or site with no corridor
    const unassignedSites = new Map<string, GroupedSite>()

    for (const station of filteredStations) {
      if (!station.coveredBySites?.length) {
        const key = '__none__'
        if (!unassignedSites.has(key)) unassignedSites.set(key, { site: { id: '', name: 'Unassigned', siteAddress: '' } as any, stations: [] })
        unassignedSites.get(key)!.stations.push(station)
        continue
      }

      for (const siteId of station.coveredBySites) {
        const site = allSites.find((s) => String((s as any).networkSiteNumber) === String(siteId))
        if (!site) continue

        const corridorId = (site as any).networkCorridorNumber?.toString() || '__none__'
        const corridor = (allCorridors as any[]).find((c) => String(c.networkCorridorNumber) === corridorId)
        const corridorName = (corridor as any)?.networkCorridorName || 'Unassigned'

        if (!corridorMap.has(corridorId)) {
          corridorMap.set(corridorId, { corridorName, corridorId, sites: [] })
        }

        const corridorEntry = corridorMap.get(corridorId)!
        let siteEntry = corridorEntry.sites.find((sg) => String((sg.site as any).networkSiteNumber) === String(siteId))
        if (!siteEntry) {
          siteEntry = { site, stations: [] }
          corridorEntry.sites.push(siteEntry)
        }

        if (!siteEntry.stations.find((st) => st.stationNumber === station.stationNumber)) {
          siteEntry.stations.push(station)
        }
      }
    }

    const result: GroupedCorridor[] = Array.from(corridorMap.values())
    // Add unassigned stations if any
    if (unassignedSites.size > 0) {
      result.push({
        corridorName: 'Unassigned',
        corridorId: '__none__',
        sites: Array.from(unassignedSites.values()),
      })
    }
    return result
  }, [filteredStations, allSites, allCorridors])

  // Flat list for pagination display count
  const startIndex = (currentPage - 1) * itemsPerPage

  const deleteStationMutation = useApiMutation('deleteSensingStation', 'DELETE', {
    onSuccess: () => {
      toast({ title: "Station Deleted", description: `Sensing station "${getStationName(selectedStation)}" has been deleted.` })
      setIsDeleteDialogOpen(false)
      setSelectedStation(null)
      queryClient.invalidateQueries({ queryKey: ["api", "getSensingStations"] })
    },
    onError: (error) => {
      toast({ title: "Deletion Failed", description: `Failed to delete station: ${error.message}`, type: "error" })
      log.error("Delete station error:", error)
    },
  })

  const getStationName = (station: SensingStation | any) => station?.name || station?.stationName || 'Unknown Station'

  const formatGPSLocation = (geoLocation: SensingStation['geoLocation']) => {
    if (!geoLocation?.latitude || !geoLocation?.longitude) return 'No location'
    return `${geoLocation.latitude.toFixed(6)}, ${geoLocation.longitude.toFixed(6)}`
  }

  const renderStationRow = (station: SensingStation) => (
    <TableRow key={station.stationNumber} className="transition-colors hover:bg-muted/50">
      <TableCell className="font-medium text-base">
        {station.stationNumber ? (
          <Link
            to="/sensnet/sensing-stations/$stationNumber"
            params={{ stationNumber: station.stationNumber.toString() }}
            className="hover:underline text-primary"
          >
            {getStationName(station)}
          </Link>
        ) : (
          getStationName(station)
        )}
      </TableCell>
      <TableCell>
        <div className="space-y-0.5">
          {station.addressIfAvailable ? (
            <div className="flex items-center gap-1 text-base">
              <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
              <span>{station.addressIfAvailable}</span>
            </div>
          ) : null}
          <div className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
            <Navigation className="h-3 w-3 shrink-0" />
            <span>{formatGPSLocation(station.geoLocation)}</span>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button asChild variant="ghost" size="icon" className="h-8 w-8">
            <Link
              to="/sensnet/sensing-stations/$stationNumber/edit"
              params={{ stationNumber: String(station.stationNumber) }}
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit {getStationName(station)}</span>
            </Link>
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10"
            onClick={() => { setSelectedStation(station); setIsDeleteDialogOpen(true) }}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load sensing stations</p>
          <Button variant="outline" onClick={() => refetch()}>Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Radio className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Sensing Stations</CardTitle>
                <CardDescription className="text-base">Manage sensing stations and coverage areas</CardDescription>
              </div>
            </div>
            <Button onClick={() => onAdd?.()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Station
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search stations by name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
                  className="pl-9"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <Select value={corridorFilter} onValueChange={handleCorridorChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by corridor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Corridors</SelectItem>
                    {allCorridors.map((corridor: any) => (
                      <SelectItem key={String(corridor.networkCorridorNumber)} value={String(corridor.networkCorridorNumber)}>
                        {corridor.networkCorridorName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-[200px]">
                <Select value={siteFilter} onValueChange={(v) => { setSiteFilter(v); setCurrentPage(1) }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sites</SelectItem>
                    {sitesForFilter.map((site: any) => (
                      <SelectItem key={String(site.networkSiteNumber)} value={String(site.networkSiteNumber)}>{site.networkSiteName || site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">

        {/* Grouped view: Corridor → Site → Stations */}
        {grouped.length > 0 ? (
          <div className="divide-y">
            {grouped.map((corridorGroup) => (
              <div key={corridorGroup.corridorId}>
                {/* Corridor header */}
                <div className="flex items-center gap-2 px-4 py-2 bg-muted/40 border-b">
                  <Network className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-base">{corridorGroup.corridorName}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {corridorGroup.sites.reduce((acc, s) => acc + s.stations.length, 0)} stations
                  </Badge>
                </div>

                {corridorGroup.sites.map((siteGroup) => (
                  <div key={(siteGroup.site as any).networkSiteNumber}>
                    {/* Site subheader */}
                    <div className="flex items-center gap-2 px-6 py-1.5 bg-muted/20 border-b">
                      <Radio className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-base font-medium">{(siteGroup.site as any).networkSiteName || siteGroup.site.name}</span>
                      {siteGroup.site.siteAddress && (
                        <span className="text-xs text-muted-foreground">— {siteGroup.site.siteAddress}</span>
                      )}
                      <Badge variant="outline" className="ml-auto text-xs">{siteGroup.stations.length}</Badge>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/10 hover:bg-muted/10">
                          <TableHead className="font-semibold text-base pl-8">Station Name</TableHead>
                          <TableHead className="font-semibold text-base">Address / GPS</TableHead>
                          <TableHead className="w-[100px] text-right font-semibold text-base">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {siteGroup.stations.map(renderStationRow)}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="h-48 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Radio className="h-10 w-10 opacity-20" />
            <p className="text-base">
              {searchQuery || siteFilter !== "all" || corridorFilter !== "all"
                ? "No stations match the current filters."
                : "No sensing stations found."}
            </p>
          </div>
        )}

        {/* Pagination — based on flat filtered list */}
        {filteredStations.length > itemsPerPage && (
          <div className="flex items-center justify-between border-t py-3">
            <p className="text-base text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredStations.length)} of {filteredStations.length} stations
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8"
                disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">Page {currentPage} of {Math.ceil(filteredStations.length / itemsPerPage)}</span>
              <Button variant="outline" size="icon" className="h-8 w-8"
                disabled={currentPage >= Math.ceil(filteredStations.length / itemsPerPage)}
                onClick={() => setCurrentPage((p) => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Sensing Station</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "<strong>{selectedStation?.name}</strong>"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setSelectedStation(null) }}
              disabled={deleteStationMutation.isPending}>Cancel</Button>
            <Button variant="destructive" onClick={() => selectedStation && deleteStationMutation.mutate({ stationNumber: selectedStation.stationNumber })}
              disabled={deleteStationMutation.isPending}>
              {deleteStationMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
